import * as React from 'react';
import { FC, useCallback, useState } from 'react';

import { VStack } from '@rbilabs/universal-components';
import { useDisclose } from 'native-base';

import { LoyaltyIncentiveSwapDialog } from 'components/loyalty-incentive-swap-dialog';
import { LoyaltyPromotionType } from 'generated/graphql-gateway';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { sumAppliedTimesApplied } from 'state/global-state/models/loyalty/rewards/rewards.utils';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { RewardRedemptionMethod } from 'state/loyalty/hooks/types';
import { useGetAvailableRewards } from 'state/loyalty/hooks/use-get-available-rewards';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { useRewardRedemptionLimit } from 'state/loyalty/hooks/use-reward-redemption-limit';
import { isCartEntryPicker } from 'utils/cart/is-cart-type';
import { HapticsNotificationType, hapticNotification } from 'utils/haptic';

import { ConfettiAnimation } from '../../../../components/ui/confetti-animation';
import { useRedeemReward } from '../hooks/use-redeem-reward-state';

import { RedeemRewardView } from './redeem-reward.view';
import { IRedeemRewardProps, RewardActions } from './types';

/**
 *
 * RedeemReward card displays below cart items that are eligible for reward redemptions
 * Users can redeem rewards using points from their loyalty balance in order to receive discounts on their order
 *
 */
export const RedeemReward: FC<React.PropsWithChildren<IRedeemRewardProps>> = ({
  isRewardApplied,
  item,
  reward,
  disabled = false,
}) => {
  const { getAvailableRewardFromCartEntry } = useGetAvailableRewards();
  const { rewardRedeemable, incentiveNotInMenu, redeemRewardLoading } = useRedeemReward(item);
  const dispatch = useAppDispatch();
  const appliedLoyaltyRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);
  const { loyaltyUser } = useLoyaltyUser();
  const rewardRedemptionLimit = useRewardRedemptionLimit();
  const { pointCost, name } = reward;
  const enableRewardUnstacking = useFlag(LaunchDarklyFlag.ENABLE_REWARD_UNSTACKING);
  const { isOpen, onOpen, onClose } = useDisclose();
  const [isRewardAdded, setIsRewardAdded] = useState(false);

  const isUnifiedMenuEnabled = useEnableUnifiedMenu();

  const handleRewardClick = useCallback(
    (action: RewardActions, force?: boolean) => async () => {
      const isPickerEntry = isCartEntryPicker(item.url);

      // If the cart entry is a picker that results in a selected product being an item,
      // and this item can be redeemed as a reward,
      // We should switch the entry url to be either item or combo.
      // This way the One Menu PDP can handle this specific picker reward edit.
      const cartEntry =
        isUnifiedMenuEnabled && isPickerEntry
          ? { ...item, url: `/menu/${item.type.toLowerCase()}-${item._id}` }
          : item;

      const shouldPromptSwap =
        action === RewardActions.ADD &&
        !force &&
        enableRewardUnstacking &&
        sumAppliedTimesApplied(appliedLoyaltyRewards) >= rewardRedemptionLimit;

      if (shouldPromptSwap) {
        return onOpen();
      }

      const { applyReward, setIsPricingRewardApplication, unApplyReward } = actions.loyalty;
      dispatch(setIsPricingRewardApplication(true));

      const cartEntryReward = getAvailableRewardFromCartEntry(cartEntry);

      if (!cartEntryReward) {
        return;
      }

      // This is to reset the animation when the reward is removed
      setIsRewardAdded(false);
      if (action === RewardActions.ADD) {
        hapticNotification({ type: HapticsNotificationType.SUCCESS });
        setIsRewardAdded(true);
      }

      if (action === RewardActions.ADD) {
        dispatch(
          applyReward({
            cartId: cartEntry.cartId,
            rewardBenefitId: cartEntryReward.rewardBenefitId,
            redemptionMethod: RewardRedemptionMethod.MOBILE,
            enableRewardUnstacking,
            rewardRedemptionLimit,
          })
        );
      } else {
        dispatch(
          unApplyReward({
            cartId: cartEntry.cartId,
            rewardBenefitId: cartEntryReward.rewardBenefitId,
            loyaltyUser,
          })
        );
      }
      hapticNotification({ type: HapticsNotificationType.SUCCESS });
    },
    [
      dispatch,
      item,
      isUnifiedMenuEnabled,
      getAvailableRewardFromCartEntry,
      loyaltyUser,
      enableRewardUnstacking,
      appliedLoyaltyRewards,
      onOpen,
      rewardRedemptionLimit,
    ]
  );

  const numberOfTimesRewardApplied = appliedLoyaltyRewards[item.cartId]?.timesApplied;

  const pointsApplied = (numberOfTimesRewardApplied || 0) * pointCost;

  if (pointCost === 0) {
    return null;
  }

  return (
    <VStack>
      <LoyaltyIncentiveSwapDialog
        incentiveType={LoyaltyPromotionType.REWARD}
        maxRewards={rewardRedemptionLimit}
        appliedRewards={sumAppliedTimesApplied(appliedLoyaltyRewards)}
        isOpen={isOpen}
        onClose={onClose}
        onSwap={() => {
          handleRewardClick(RewardActions.ADD, true)();
          onClose();
        }}
      />
      <RedeemRewardView
        onRewardAdd={handleRewardClick(RewardActions.ADD)}
        onRewardRemove={handleRewardClick(RewardActions.REMOVE)}
        isRewardApplied={isRewardApplied}
        points={pointCost}
        rewardTitle={name}
        disableApplyButton={!rewardRedeemable || disabled}
        pointsApplied={pointsApplied}
        itemQuantity={item.quantity}
        incentiveNotInMenu={incentiveNotInMenu}
        redeemRewardLoading={redeemRewardLoading}
      />
      {isRewardAdded && (
        <ConfettiAnimation
          onAnimationFinish={() => {
            setIsRewardAdded(false);
          }}
        />
      )}
    </VStack>
  );
};
